// src/pages/purchase/FinalizeSitePurchase.js
// Direct Site Purchase — Stage 2: Manager/SM/Owner reviews the Site
// Engineer's confirmed quantities, checks the rate, and finalizes.
// This is the ONLY step that writes to site_inventory_summary — same
// authority boundary as ReceiveMaterial.js.
//
// D-291 P3d-4:
//   - Finalize writes NO vendor payable and asks for no invoice no. / date:
//     the vendor's bill is entered in Awaiting Bills afterwards
//     (purchaseBillService.js), with the duplicate-invoice check.
//   - Rate pre-fill = stockRateFromPOLine(): before GST when the company
//     claims ITC, incl. GST when it does not.
//   - A "Rate not agreed yet" line shows that, not "PO rate ₹0".
//
// D-295 (08 Oct 2026):
//   - Rate is prefilled from the PO (editable). The PO's discount, GST and
//     landed rate are shown for reference only.
//   - Lines are matched to the PO with the SAME function the service uses
//     (matchConfirmedToLines), so screen and service always agree.
//   - Short delivery needs a reason (same rule as the normal GRN).
//   - Actual freight (reference only).
//   - A PO stuck in "finalizing" is shown clearly and cannot be finalized
//     again from here.

import { useEffect, useState } from "react";
import { db } from "../../firebase";
import { doc, getDoc } from "firebase/firestore";
import { useParams, useNavigate } from "react-router-dom";
import { stockRateFromPOLine } from "../../utils/grnPlan";
import { companyClaimsItc, itcOnPurchase } from "../../utils/gstBill";
import { SITE_FINALIZE_ROLES, hasPoRole } from "../../utils/poRoles";
import {
  finalizeSitePurchaseGRN,
  matchConfirmedToLines,
} from "../../services/directSitePurchaseService";

const userData = () => JSON.parse(localStorage.getItem("user") || "{}");
// Who may finalize: SITE_FINALIZE_ROLES from src/utils/poRoles.js (HIGH-01).

const inr = (n, digits = 2) =>
  Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: digits });

export default function FinalizeSitePurchase() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const me       = userData();

  const [po,           setPo]           = useState(null);
  const [lines,        setLines]        = useState([]);
  const [itemRates,    setItemRates]    = useState({});
  const [shortReason,  setShortReason]  = useState("");
  const [actualFreight, setActualFreight] = useState("");
  // D-291 P3d-4: company claims GST input credit? -> rate before GST.
  const [itcClaimed,   setItcClaimed]   = useState(false);
  const [vendorBlocksItc, setVendorBlocksItc] = useState(false); // P3d-4c
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [submitting,   setSubmitting]   = useState(false);

  const canFinalize = hasPoRole(SITE_FINALIZE_ROLES, me.role);

  useEffect(() => { if (id) fetchPO(); }, [id]); // eslint-disable-line

  const fetchPO = async () => {
    setLoading(true);
    setError(null);
    try {
      const snap = await getDoc(doc(db, "purchase_orders", id));
      if (!snap.exists()) { setError("Purchase order not found."); return; }

      const poData = { id: snap.id, ...snap.data() };

      if (poData.company_id !== me.company_id) { setError("Access denied."); return; }
      if (poData.purchase_mode !== "direct_site_purchase") {
        setError("This is not a Direct Site Purchase order."); return;
      }
      if (poData.grn_locked) { setError("Already finalized."); return; }
      if (poData.site_confirmation_status === "finalizing") {
        setError(
          `Finalization was started by ${poData.finalizing_by_name || "a manager"} and did not finish. ` +
          `Do not try again — ask admin to check this PO's site stock first.`
        );
        return;
      }
      if (poData.site_confirmation_status !== "site_confirmed") {
        setError("Site Engineer has not confirmed receipt yet."); return;
      }

      // Same matching the service will do — if it refuses, show why now
      // instead of letting the manager fill the form first.
      const matched = matchConfirmedToLines(poData);

      // D-291 P3d-4 / P3d-4c: rate before GST only when the company claims
      // ITC (Company Profile) AND this vendor's GSTIN is valid (Vendor Master).
      // Anything unreadable -> incl. GST (never claim credit by mistake).
      let itc = false;
      let vendorBlocks = false;
      try {
        const cSnap = await getDoc(doc(db, "companies", me.company_id));
        const company = cSnap.exists() ? cSnap.data() : null;
        let vendor = null;
        if (poData.vendor_id) {
          const vSnap = await getDoc(doc(db, "vendors", poData.vendor_id));
          const v = vSnap.exists() ? vSnap.data() : null;
          vendor = v && v.company_id === me.company_id ? v : null;
        }
        itc = itcOnPurchase(company, vendor);
        vendorBlocks = companyClaimsItc(company) && !itc;
      } catch (e) {
        console.warn("[FinalizeSitePurchase] company / vendor not readable - rate pre-fill incl. GST:", e?.code || e);
      }
      setItcClaimed(itc);
      setVendorBlocksItc(vendorBlocks);

      // Prefill each received line's rate from the PO — after discount,
      // before GST when the company claims ITC (incl. GST when not), excl.
      // freight (D-304 C / D-291 P3d-4). Editable.
      const prefill = {};
      for (const m of matched) {
        const poRate = stockRateFromPOLine(m.line, { itcClaimed: itc });
        if (m.received_qty > 0 && poRate > 0) {
          prefill[m.key] = String(poRate);
        }
      }

      setPo(poData);
      setLines(matched);
      setItemRates(prefill);
    } catch (err) {
      console.error("[FinalizeSitePurchase] fetchPO error:", err);
      setError(err.message || "Failed to load purchase order.");
    } finally {
      setLoading(false);
    }
  };

  const rateOf = (key) => Number(itemRates[key] || 0);

  const billTotal = lines.reduce((sum, m) => sum + m.received_qty * rateOf(m.key), 0);

  // Reference only — what the PO's landed cost (after discount, with freight
  // share and GST) would be for the quantity actually received.
  const landedTotal = lines.reduce(
    (sum, m) => sum + m.received_qty * Number(m.line.landed_rate || 0), 0
  );
  const hasLanded = lines.some(m => Number(m.line.landed_rate || 0) > 0);

  const shortLines = lines.filter(m => m.received_qty < Number(m.line.ordered_qty ?? m.line.qty ?? 0));


  const handleFinalize = async () => {
    if (submitting) return;
    if (!canFinalize) {
      alert("Only Manager / SM / Owner can finalize a Direct Site Purchase.");
      return;
    }

    const missingRate = lines.find(m =>
      m.received_qty > 0 && !m.line.is_custom_item && !(rateOf(m.key) > 0)
    );
    if (missingRate) {
      alert(`Rate missing for "${missingRate.line.item_name}". Enter a rate for every received item.`);
      return;
    }
    if (shortLines.length > 0 && !shortReason.trim()) {
      alert("Some items arrived short. Enter a reason — the remaining quantity will be closed.");
      return;
    }
    if (Number(actualFreight || 0) < 0) {
      alert("Actual freight cannot be negative.");
      return;
    }

    const confirmText =
      `Finalize this Direct Site Purchase?\n\n` +
      `Received value: ₹${inr(billTotal)}\n` +
      (shortLines.length > 0 ? `Short items will be closed: ${shortLines.length}\n` : "") +
      `\nThis credits site stock. It cannot be undone.\n` +
      `The vendor's bill is entered next, in Awaiting Bills.`;
    if (!window.confirm(confirmText)) return;

    setSubmitting(true);
    try {
      const rates = {};
      for (const m of lines) rates[m.key] = rateOf(m.key);

      const result = await finalizeSitePurchaseGRN({
        po_id:               id,
        company_id:          me.company_id,
        rates,
        short_close_reason:  shortReason.trim(),
        actual_freight:      Number(actualFreight || 0),
        completed_by:        me.uid || me.id || "",
        completed_by_name:   me.name || "",
      });

      alert(
        `Direct Site Purchase finalized.\n` +
        `Received value: ₹${inr(result.grand_total)}\n` +
        `Site stock updated.` +
        (result.bill_status === "awaiting_bill"
          ? `\n\nNext: when the vendor's bill comes, press 'Enter bill' on the PO page - the bill adds it to the vendor's khaata.`
          : "")
      );
      navigate(`/purchase-orders/${id}`, { replace: true });

    } catch (err) {
      console.error("[FinalizeSitePurchase] error:", err);
      alert(err.message || "Finalization failed.");
      // Only when finalize stopped after the claim: reload so the PO's
      // "finalizing" warning shows instead of the form. A plain validation
      // error leaves the PO untouched, so the manager's entries are kept.
      if (String(err.message || "").includes("stopped part-way")) fetchPO();
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <p className="text-gray-500 text-sm">Loading...</p>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-50 p-6 max-w-2xl mx-auto">
      <button onClick={() => navigate(-1)} className="mb-4 text-sm text-slate-500 hover:text-slate-800">← Back</button>
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-700 font-medium">{error}</p>
      </div>
    </div>
  );

  if (!po) return null;

  return (
    <div className="min-h-screen bg-gray-50 p-4 max-w-3xl mx-auto">
      <button onClick={() => navigate(-1)} className="mb-4 text-sm text-slate-500 hover:text-slate-800">← Back</button>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-xl font-bold text-slate-800">Finalize Direct Site Purchase</h2>
            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">Direct Site Purchase</span>
          </div>
          <p className="text-xs text-slate-400">
            PO: {po.po_number || id} • {po.vendor_name || ""} • {po.project_name} — {po.site_name}
          </p>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-5 text-xs text-green-800">
          <strong>Confirmed by:</strong> {po.site_confirmed_by_name || "Site Engineer"}.
          Check the rate for each item — it is filled from the PO,{" "}
          {itcClaimed
            ? "before GST (your company claims GST input credit)"
            : vendorBlocksItc
              ? "including GST (this vendor's GSTIN is missing or not valid, so GST input credit cannot be claimed - fix it in Vendor Master if the vendor is registered)"
              : "including GST"}.
          Finalizing credits site stock (central store stock is not affected). The vendor's bill
          is entered next, in Awaiting Bills — that adds it to the vendor's khaata.
        </div>

        {!canFinalize && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-5 text-xs text-red-700 font-semibold">
            ⛔ Only Manager / SM / Owner can finalize this.
          </div>
        )}

        <div className="space-y-3 mb-5">
          {lines.map(m => {
            const line     = m.line;
            const ordered  = Number(line.ordered_qty ?? line.qty ?? 0);
            const received = m.received_qty;
            const isShort  = received < ordered;
            const isOver   = received > ordered;
            const amount   = received * rateOf(m.key);
            return (
              <div key={m.key} className={`border rounded-2xl p-4 ${
                isShort ? "border-amber-200 bg-amber-50" :
                isOver  ? "border-orange-200 bg-orange-50" :
                          "border-slate-200 bg-white"
              }`}>
                <div className="flex justify-between items-start gap-3">
                  <div className="flex-1">
                    <p className="font-semibold text-slate-800 text-sm">{line.item_name}</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      <span className="text-xs text-slate-500">Ordered: {inr(ordered)} {line.unit || "nos"}</span>
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                        Received: {inr(received)} {line.unit || "nos"}
                      </span>
                      {isShort && (
                        <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-medium">
                          Short — {inr(ordered - received)} will be closed
                        </span>
                      )}
                      {isOver && (
                        <span className="text-xs bg-orange-100 text-orange-800 px-2 py-0.5 rounded-full font-medium">
                          Over +{inr(received - ordered)}
                        </span>
                      )}
                      {line.is_custom_item && (
                        <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full">⚡ Custom — not added to site stock</span>
                      )}
                    </div>
                    {/* PO reference — never changes the bill */}
                    <p className="text-[11px] text-slate-400 mt-1.5">
                      {line.rate_not_agreed === true
                        ? <span className="text-amber-600 font-medium">Rate not agreed on the PO — type the rate from the vendor's bill</span>
                        : <>PO rate ₹{inr(line.base_rate ?? line.rate)} before GST</>}
                      {Number(line.discount_value || 0) > 0 &&
                        ` · discount ${line.discount_type === "flat" ? "₹" : ""}${inr(line.discount_value)}${line.discount_type === "flat" ? "" : "%"}`}
                      {line.gst_percent != null && ` · GST ${inr(line.gst_percent)}%`}
                      {Number(line.landed_rate || 0) > 0 && ` · landed ₹${inr(line.landed_rate)}/unit`}
                    </p>
                  </div>
                  {received > 0 && (
                    <div className="flex flex-col items-end gap-1">
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-slate-400">₹</span>
                        <input
                          type="number" min="0" step="0.01"
                          placeholder={line.is_custom_item ? "Rate (optional)" : "Rate *"}
                          value={itemRates[m.key] ?? ""}
                          onChange={e => setItemRates(prev => ({ ...prev, [m.key]: e.target.value }))}
                          disabled={!canFinalize}
                          className="w-28 border border-slate-200 rounded-xl px-2 py-1.5 text-right text-sm outline-none focus:ring-2 focus:ring-cyan-300 disabled:opacity-50 bg-white"
                        />
                      </div>
                      {(() => {
                        const poRate  = stockRateFromPOLine(line, { itcClaimed });
                        const entered = rateOf(m.key);
                        if (!(poRate > 0) || !(entered > 0) || Math.abs(entered - poRate) / poRate < 0.005) return null;
                        const pct = ((entered - poRate) / poRate) * 100;
                        return (
                          <span className="text-[11px] bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full whitespace-nowrap">
                            PO ₹{inr(poRate)} → {pct > 0 ? "+" : ""}{inr(pct, 1)}%
                          </span>
                        );
                      })()}
                      {amount > 0 && (
                        <span className="text-xs text-slate-500 whitespace-nowrap">= ₹{inr(amount)}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {shortLines.length > 0 && (
          <div className="mb-5">
            <label className="block text-xs font-semibold text-amber-800 mb-1">
              Reason for short delivery * — the remaining quantity will be closed
            </label>
            <textarea
              rows={2}
              value={shortReason}
              onChange={e => setShortReason(e.target.value)}
              disabled={!canFinalize}
              placeholder="e.g. Vendor sent 3 of 5, balance not needed"
              className="w-full border border-amber-300 rounded-xl p-3 text-sm resize-none outline-none focus:ring-2 focus:ring-amber-300 disabled:opacity-50"
            />
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
          <div>
            <label className="block text-xs text-slate-500 mb-1">
              Actual freight (optional, PO estimate ₹{inr(po.freight_amount)})
            </label>
            <input
              type="number" min="0" step="0.01" value={actualFreight}
              onChange={e => setActualFreight(e.target.value)}
              disabled={!canFinalize}
              placeholder="0"
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-cyan-300 disabled:opacity-50"
            />
          </div>
        </div>

        {hasLanded && (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 mb-5 text-xs text-slate-600">
            PO landed cost for the received quantity: <strong>₹{inr(landedTotal)}</strong> ·
            this bill: <strong>₹{inr(billTotal)}</strong>
            {Math.abs(landedTotal - billTotal) >= 1 && (
              <> · difference <strong>₹{inr(landedTotal - billTotal)}</strong></>
            )}
            <br />
            The rate is filled from the PO after discount, {itcClaimed ? "before GST" : "including GST"} (freight not included). Change it only if the vendor's invoice differs.
          </div>
        )}

        <div className="flex items-center justify-between mb-5 px-1">
          <span className="text-sm text-slate-500">Received value</span>
          <span className="text-xl font-bold text-slate-800">₹{inr(billTotal)}</span>
        </div>

        <button
          onClick={handleFinalize}
          disabled={submitting || !canFinalize}
          className="w-full py-3 rounded-xl bg-green-600 text-white font-semibold text-sm disabled:opacity-40 hover:bg-green-700 transition">
          {submitting ? "Finalizing..." : "✓ Finalize & Update Site Stock"}
        </button>
      </div>
    </div>
  );
}